import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from '@/common/ui/select'
import useMutationUpdateGeneralSetting from '@/modules/client-setting/api/hooks/useMutationUpdateGeneralSetting'
import { GetGeneralSettingResponse } from '@/modules/client-setting/type'
import { useState } from 'react'
import { toast } from 'sonner'
import showErrorToast from '@/common/utils/showErrorToast'
import { useQueryClient } from '@tanstack/react-query'
import { GeneralSettingQueryKeys } from '@/modules/client-setting/keys'
import useHasPermission from '@/common/hooks/useHasPermission'
import { PermissionKeys } from '@/modules/manage-member/mappers'
import { CarouselItemSize } from '@/schema'
import useQueryGetCarousel from '@/modules/client-setting/api/hooks/useQueryGetCarousel'
import { Modal } from '@/common/ui/modal'
import { Button } from '@/common/ui/button'

interface CarouselItemSizeProps {
	data: GetGeneralSettingResponse
}

export function CarouselItemSizeComponent({ data }: CarouselItemSizeProps) {
	const queryClient = useQueryClient()
	const [isLoading, setIsLoading] = useState(false)
	const [showConfirmModal, setShowConfirmModal] = useState(false)
	const [pendingValue, setPendingValue] = useState<string | null>(null)
	const { mutateAsync: updateGeneralSetting } =
		useMutationUpdateGeneralSetting()
	const hasWritePermission = useHasPermission(PermissionKeys.WEB_SETTING)
	const { data: carouselData } = useQueryGetCarousel()

	const getCurrentSizeLabel = () => {
		const size = data?.carouselItemSize ?? CarouselItemSize.LARGE
		if (size === CarouselItemSize.SMALL) return '16:7'
		if (size === CarouselItemSize.MEDIUM) return '16:5'
		return '16:9'
	}

	const getNewSizeLabel = (value: string) => {
		if (value === CarouselItemSize.SMALL) return '16:7'
		if (value === CarouselItemSize.MEDIUM) return '16:5'
		return '16:9'
	}

	const handleChange = async (value: string) => {
		if (!hasWritePermission) return

		// Check if size is actually changing
		const currentSize = data?.carouselItemSize ?? CarouselItemSize.LARGE
		if (currentSize === value) return

		// Check if there are existing carousel items
		const hasExistingImages = carouselData?.data && carouselData.data.length > 0

		if (hasExistingImages) {
			// Show confirmation modal
			setPendingValue(value)
			setShowConfirmModal(true)
		} else {
			// No existing images, proceed directly
			await updateSize(value)
		}
	}

	const updateSize = async (value: string) => {
		setIsLoading(true)
		try {
			await updateGeneralSetting({
				payload: {
					...data,
					carouselItemSize: value as CarouselItemSize,
				},
			})
			toast.success('อัพเดทขนาดรูปสไลด์สำเร็จ')
			queryClient.invalidateQueries({
				queryKey: [GeneralSettingQueryKeys.GetGeneralSetting],
			})
		} catch (error) {
			showErrorToast(error as Error)
		} finally {
			setIsLoading(false)
		}
	}

	const handleConfirm = async () => {
		if (pendingValue) {
			await updateSize(pendingValue)
			setShowConfirmModal(false)
			setPendingValue(null)
		}
	}

	const handleCancel = () => {
		setShowConfirmModal(false)
		setPendingValue(null)
	}

	return (
		<>
			<Select
				value={data?.carouselItemSize ?? CarouselItemSize.LARGE}
				onValueChange={handleChange}
				disabled={isLoading || !hasWritePermission}
			>
				<SelectTrigger className="w-[180px]">
					<SelectValue placeholder="เลือกขนาด" />
				</SelectTrigger>
				<SelectContent>
					<SelectItem value={CarouselItemSize.MEDIUM}>เล็ก (16:5)</SelectItem>
					<SelectItem value={CarouselItemSize.SMALL}>กลาง (16:7)</SelectItem>
					<SelectItem value={CarouselItemSize.LARGE}>ใหญ่ (16:9)</SelectItem>
				</SelectContent>
			</Select>

			<Modal
				isOpen={showConfirmModal}
				onClose={handleCancel}
				title="คุณแน่ใจหรือไม่?"
			>
				<div className="space-y-4">
					<div className="space-y-2">
						<p>
							คุณกำลังเปลี่ยนขนาดรูปสไลด์จาก{' '}
							<span className="font-semibold">{getCurrentSizeLabel()}</span>{' '}
							{'เป็น'}{' '}
							<span className="font-semibold">
								{pendingValue ? getNewSizeLabel(pendingValue) : ''}
							</span>
						</p>
						<p className="text-amber-600 dark:text-amber-400">
							⚠️ สัดส่วนบนเว็บจะโชว์เป็น{' '}
							<span className="font-semibold">
								{pendingValue ? getNewSizeLabel(pendingValue) : ''}
							</span>{' '}
							โปรดเช็คว่ารูปภาพเก่าที่มีอยู่เป็นสัดส่วน{' '}
							<span className="font-semibold">
								{pendingValue ? getNewSizeLabel(pendingValue) : ''}
							</span>{' '}
							เพื่อไม่ให้รูปผิดเพี้ยน
						</p>
					</div>
					<div className="flex w-full items-center justify-end space-x-2">
						<Button
							disabled={isLoading}
							variant="outline"
							onClick={handleCancel}
						>
							ยกเลิก
						</Button>
						<Button disabled={isLoading} onClick={handleConfirm}>
							ยืนยัน
						</Button>
					</div>
				</div>
			</Modal>
		</>
	)
}
